import React, { useEffect, useRef, useState } from "react";
import api, { inr, today, formatApiError } from "../lib/api";
import { Download, Play, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { BranchFilter } from "./BranchTools";

const field = "h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const NUM = new Set(["amount", "discount", "paid_amount", "balance", "monthly_salary", "present_days", "paid_days", "net", "advance_total", "net_paid", "count"]);
const monthStart = () => today().slice(0, 8) + "01";

const ResultTable = ({ res }) => (
  <div className="overflow-auto max-h-[65vh] border border-border rounded-md">
    <table data-testid="custom-report-table" className="w-full text-sm">
      <thead className="bg-muted sticky top-0 text-xs uppercase text-muted-foreground">
        <tr>{res.columns.map((c) => <th key={c.key} className={`px-3 py-2 whitespace-nowrap ${NUM.has(c.key) ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {res.rows.map((r, i) => (
          <tr key={i} className="border-t border-border">
            {res.columns.map((c) => <td key={c.key} className={`px-3 py-1.5 whitespace-nowrap ${NUM.has(c.key) ? "text-right num" : ""}`}>{NUM.has(c.key) && c.key !== "count" && !c.key.includes("days") ? inr(r[c.key]) : r[c.key]}</td>)}
          </tr>
        ))}
      </tbody>
      <tfoot className="bg-muted font-bold">
        <tr data-testid="custom-report-totals">
          {res.columns.map((c, i) => <td key={c.key} className={`px-3 py-2 ${NUM.has(c.key) ? "text-right num" : ""}`}>{i === 0 ? `Total (${res.total_rows})` : (res.totals[c.key] !== undefined ? (c.key === "count" || c.key.includes("days") ? res.totals[c.key] : inr(res.totals[c.key])) : "")}</td>)}
        </tr>
      </tfoot>
    </table>
  </div>
);

export const CustomReport = () => {
  const [meta, setMeta] = useState(null);
  const [source, setSource] = useState("cashbook");
  const [cols, setCols] = useState([]);
  const [groupBy, setGroupBy] = useState("");
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(today());
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [branch, setBranch] = useState("");
  const pendingTpl = useRef(null);

  const loadMeta = () => api.get("/reports/custom/meta").then((r) => setMeta(r.data)).catch(() => toast.error("Could not load report options"));
  useEffect(() => { loadMeta(); }, []);
  useEffect(() => {
    if (!meta) { return; }
    const t = pendingTpl.current;
    pendingTpl.current = null;
    setCols(t ? t.columns : meta.sources[source].columns.map((c) => c.key));
    setGroupBy(t ? t.group_by || "" : "");
    setRes(null);
  }, [source, meta]);

  if (!meta) return <div className="h-40 animate-pulse bg-muted rounded-lg" />;
  const all = meta.sources[source].columns;
  const toggle = (k) => setCols((c) => (c.includes(k) ? c.filter((x) => x !== k) : all.map((a) => a.key).filter((x) => x === k || c.includes(x))));
  const params = (format) => ({ source, columns: cols.join(","), start, end, group_by: groupBy || undefined, format, branch: branch || undefined });

  const run = async () => {
    if (!cols.length) return toast.error("Pick at least one column");
    setBusy(true);
    try { setRes((await api.get("/reports/custom", { params: params("json") })).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const download = async (format) => {
    try {
      const r = await api.get("/reports/custom", { params: params(format), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${source}_report_${start}_${end}.${format}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const saveTpl = async () => {
    const name = window.prompt("Save this report layout as:", "");
    if (!name || !name.trim()) return;
    try { await api.post("/reports/templates", { name, source, columns: cols, group_by: groupBy }); toast.success("Report layout saved"); loadMeta(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const applyTpl = (t) => {
    if (t.source === source) { setCols(t.columns); setGroupBy(t.group_by || ""); return; }
    pendingTpl.current = t;
    setSource(t.source);
  };
  const delTpl = async (t) => { await api.delete(`/reports/templates/${t.id}`); loadMeta(); };

  return (
    <div data-testid="custom-report" className="space-y-4">
      {meta.templates.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs text-muted-foreground">Saved layouts:</span>
          {meta.templates.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 rounded-full border border-input bg-card pl-3 pr-1 h-8 text-xs">
              <button data-testid={`tpl-use-${t.id}`} onClick={() => applyTpl(t)} className="hover:text-primary">{t.name}</button>
              <button data-testid={`tpl-del-${t.id}`} onClick={() => delTpl(t)} className="h-6 w-6 flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash2 size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="rounded-xl border border-border bg-card p-4 space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div><div className="text-xs text-muted-foreground mb-1">Data</div>
            <select data-testid="custom-source" className={field} value={source} onChange={(e) => setSource(e.target.value)}>
              {Object.entries(meta.sources).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
            </select></div>
          <div><div className="text-xs text-muted-foreground mb-1">From</div><input data-testid="custom-start" type="date" className={field} value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div><div className="text-xs text-muted-foreground mb-1">To</div><input data-testid="custom-end" type="date" className={field} value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          <BranchFilter value={branch} onChange={setBranch} testid="custom-branch" />
          <div><div className="text-xs text-muted-foreground mb-1">Summarise by</div>
            <select data-testid="custom-groupby" className={field} value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              <option value="">No grouping (every entry)</option>
              {branch && <option value="branch">Branch</option>}
              {all.filter((c) => !NUM.has(c.key)).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select></div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1.5">Columns ({cols.length}/{all.length}) · <button data-testid="custom-all" className="underline" onClick={() => setCols(all.map((c) => c.key))}>all</button> · <button data-testid="custom-none" className="underline" onClick={() => setCols([])}>none</button></div>
          <div className="flex flex-wrap gap-1.5">
            {all.map((c) => (
              <button key={c.key} data-testid={`custom-col-${c.key}`} onClick={() => toggle(c.key)}
                className={`h-8 px-3 rounded-full text-xs border transition-colors ${cols.includes(c.key) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input text-muted-foreground hover:bg-muted"}`}>{c.label}</button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="custom-run" onClick={run} disabled={busy} className="h-10 px-5 rounded-md bg-primary text-primary-foreground text-sm flex items-center gap-1.5 disabled:opacity-50"><Play size={14} /> {busy ? "Loading…" : "Show report"}</button>
          <button data-testid="custom-xlsx" onClick={() => download("xlsx")} className="h-10 px-4 rounded-md border border-input text-sm flex items-center gap-1.5 hover:bg-muted"><Download size={14} /> Excel</button>
          <button data-testid="custom-csv" onClick={() => download("csv")} className="h-10 px-4 rounded-md border border-input text-sm flex items-center gap-1.5 hover:bg-muted"><Download size={14} /> CSV</button>
          <button data-testid="custom-save" onClick={saveTpl} className="h-10 px-4 rounded-md border border-input text-sm flex items-center gap-1.5 hover:bg-muted"><Save size={14} /> Save layout</button>
        </div>
      </div>
      {res && (res.total_rows > res.rows.length) && <div className="text-xs text-muted-foreground">Showing first {res.rows.length} of {res.total_rows} rows — download Excel for all.</div>}
      {res && <ResultTable res={res} />}
    </div>
  );
};
